/**
 * VendaItemDialog Component - Diálogo para Adicionar/Editar Item da Venda
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { VendaItem } from "@/types/venda";
import { vendaItemSchema, VendaItemFormData } from "@/schemas/vendaSchema";
import { TipoProdutoCombobox } from "@/components/tipo-produto/TipoProdutoCombobox";
import { FornadaCombobox } from "@/components/fornada/FornadaCombobox";
import { tipoProdutosAdapter } from "@/data";

interface VendaItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: VendaItem | null;
  onSave: (item: VendaItem) => void;
}

export function VendaItemDialog({
  open,
  onOpenChange,
  item,
  onSave,
}: VendaItemDialogProps) {
  const [selectedProdutoId, setSelectedProdutoId] = useState<string>("");
  const [selectedFornadaId, setSelectedFornadaId] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<VendaItemFormData>({
    resolver: zodResolver(vendaItemSchema),
    defaultValues: {
      quantidade: 1,
      desconto_percentual: 0,
      desconto_valor: 0,
      brinde: false,
    },
  });

  useEffect(() => {
    if (open) {
      if (item) {
        reset(item);
        setSelectedProdutoId(item.tipo_produto_id);
        setSelectedFornadaId((item as any).id_fornada || "");
      } else {
        reset({
          tipo_produto_id: "",
          produto_nome: "",
          quantidade: 1,
          unidade_medida: "un",
          preco_unitario: 0,
          desconto_percentual: 0,
          desconto_valor: 0,
          brinde: false,
          subtotal: 0,
        });
        setSelectedProdutoId("");
        setSelectedFornadaId("");
      }
    }
  }, [open, item, reset]);

  // Buscar dados do produto quando selecionado
  const handleProdutoSelect = (produto: any) => {
    if (produto) {
      setSelectedProdutoId(produto.id_tipo_produto);
      setValue("tipo_produto_id", produto.id_tipo_produto);
      setValue("produto_nome", produto.nome);
      setValue("preco_unitario", produto.preco_base);
      setValue("unidade_medida", produto.unidade_venda);
    } else {
      setSelectedProdutoId("");
    }
  };

  // Buscar dados da fornada quando selecionada
  const handleFornadaSelect = (fornada: any) => {
    if (fornada) {
      setSelectedFornadaId(fornada.id_fornada);
      setValue("preco_unitario", fornada.custo_medio_unitario || 0);
    } else {
      setSelectedFornadaId("");
    }
  };

  // Calcular subtotal
  const quantidade = watch("quantidade") || 0;
  const precoUnitario = watch("preco_unitario") || 0;
  const descontoPercentual = watch("desconto_percentual") || 0;
  const descontoValor = watch("desconto_valor") || 0;
  const brinde = watch("brinde") || false;

  useEffect(() => {
    let subtotal = quantidade * precoUnitario;
    
    if (brinde) {
      subtotal = 0;
    } else {
      // Aplicar desconto percentual
      if (descontoPercentual > 0) {
        subtotal = subtotal * (1 - descontoPercentual / 100);
      }
      // Aplicar desconto em valor
      if (descontoValor > 0) {
        subtotal = subtotal - descontoValor;
      }
    }

    setValue("subtotal", Math.max(0, subtotal));
  }, [quantidade, precoUnitario, descontoPercentual, descontoValor, brinde, setValue]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    // Previne submit ao pressionar Enter em campos que não sejam botões
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
      e.preventDefault();
    }
  };

  const handleFormSubmit = handleSubmit((data) => {
    try {
      const itemData = {
        ...data,
        id_fornada: selectedFornadaId || undefined,
      };
      onSave(itemData as VendaItem);
      reset();
      setSelectedProdutoId("");
      setSelectedFornadaId("");
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao salvar item:", error);
    }
  });

  const subtotal = watch("subtotal");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {item ? "Editar Item" : "Adicionar Item"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleFormSubmit} onKeyDown={handleKeyDown} className="space-y-4">
          <div>
            <Label>Produto *</Label>
            <TipoProdutoCombobox
              value={selectedProdutoId}
              onSelect={handleProdutoSelect}
            />
            {errors.tipo_produto_id && (
              <p className="text-sm text-destructive mt-1">
                {errors.tipo_produto_id.message}
              </p>
            )}
          </div>

          <div>
            <Label>Fornada de Origem (opcional)</Label>
            <FornadaCombobox
              value={selectedFornadaId}
              onSelect={handleFornadaSelect}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Ao selecionar uma fornada, o preço será preenchido automaticamente com o custo médio
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="quantidade">Quantidade *</Label>
              <Input
                id="quantidade"
                type="number"
                step="0.001"
                {...register("quantidade", { valueAsNumber: true })}
              />
              {errors.quantidade && (
                <p className="text-sm text-destructive mt-1">
                  {errors.quantidade.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="unidade_medida">Unidade de Medida *</Label>
              <Input id="unidade_medida" {...register("unidade_medida")} />
              {errors.unidade_medida && (
                <p className="text-sm text-destructive mt-1">
                  {errors.unidade_medida.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="preco_unitario">Preço Unitário *</Label>
            <Input
              id="preco_unitario"
              type="number"
              step="0.01"
              {...register("preco_unitario", { valueAsNumber: true })}
            />
            {errors.preco_unitario && (
              <p className="text-sm text-destructive mt-1">
                {errors.preco_unitario.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="desconto_percentual">Desconto (%)</Label>
              <Input
                id="desconto_percentual"
                type="number"
                step="0.01"
                {...register("desconto_percentual", { valueAsNumber: true })}
              />
            </div>

            <div>
              <Label htmlFor="desconto_valor">Desconto (R$)</Label>
              <Input
                id="desconto_valor"
                type="number"
                step="0.01"
                {...register("desconto_valor", { valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="brinde"
              checked={watch("brinde")}
              onCheckedChange={(checked) => setValue("brinde", checked)}
            />
            <Label htmlFor="brinde">Este item é um brinde</Label>
          </div>

          <div>
            <Label htmlFor="observacoes_item">Observações</Label>
            <Textarea id="observacoes_item" {...register("observacoes_item")} rows={2} />
          </div>

          <div className="flex justify-between items-center p-4 bg-muted rounded-md">
            <span className="font-semibold">Subtotal:</span>
            <span className="text-xl font-bold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(subtotal)}
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              {item ? "Salvar" : "Adicionar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
